import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";
import { ResultRow, animalNames, CHART_COLORS } from "./constants";

interface GruposTabProps {
  filtered: ResultRow[];
}

const GruposTab: React.FC<GruposTabProps> = ({ filtered }) => {
  const mostrarGrafico = false;
  const grupoCount: Record<number, number> = {};
  filtered.forEach(r => {
    grupoCount[r.grupo] = (grupoCount[r.grupo] || 0) + 1;
  });

  const grupoChart = Object.entries(grupoCount)
    .map(([g, count]) => ({
      grupo: parseInt(g),
      name: `G${g.padStart(2, '0')} ${animalNames[parseInt(g)] || ''}`,
      count,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 15);

  const avg = grupoChart.length > 0
    ? grupoChart.reduce((s, g) => s + g.count, 0) / grupoChart.length
    : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Grupos Mais Sorteados (Top 15)</CardTitle>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <TrendingUp className="h-3 w-3 text-green-500" /> Acima da média
          </span>
          <span className="flex items-center gap-1">
            <TrendingDown className="h-3 w-3 text-red-500" /> Abaixo da média
          </span>
        </div>
      </CardHeader>
      <CardContent className="px-2 sm:px-6">
        {mostrarGrafico && <div className="h-[350px] sm:h-[450px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={grupoChart} layout="vertical" margin={{ left: 80, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={75} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="count" name="Vezes" radius={[0, 4, 4, 0]}>
                {grupoChart.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={entry.count >= avg
                      ? 'hsl(var(--chart-1))'
                      : 'hsl(var(--chart-5))'
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>}
        {/* Hot/Cold table below chart */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
          {grupoChart.map(g => {
            const isHot = g.count >= avg;
            return (
              <div
                key={g.grupo}
                className={`flex items-center justify-between p-2 rounded-lg border text-sm ${
                  isHot ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'
                }`}
              >
                <span className="font-medium">G{String(g.grupo).padStart(2, '0')}</span>
                <span className="flex items-center gap-1">
                  {g.count}x
                  {isHot
                    ? <TrendingUp className="h-3 w-3 text-green-500" />
                    : <TrendingDown className="h-3 w-3 text-red-500" />
                  }
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default GruposTab;
